/**
 * TypeBreakdownChart - Card type distribution donut chart
 *
 * Displays card counts by type (creatures, spells, lands, etc.) using Chart.js.
 */
import type { ChartOptions } from 'chart.js';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';

import InfoButton, { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

import type { TypeBreakdown } from '../../lib/deckStatistics';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

export interface TypeBreakdownChartProps {
  data: TypeBreakdown;
  /** Compact mode for left panel */
  compact?: boolean;
}

/** Type colors matching MTG aesthetics */
const TYPE_COLORS = {
  creatures: '#22c55e', // Green for creatures
  instants: '#3b82f6', // Blue for instants
  sorceries: '#8b5cf6', // Purple for sorceries
  artifacts: '#6b7280', // Gray for artifacts
  enchantments: '#f59e0b', // Amber for enchantments
  planeswalkers: '#f97316', // Orange for planeswalkers
  lands: '#92400e', // Brown for lands
  battles: '#ef4444', // Red for battles
  other: '#525252', // Neutral for other
};

const TYPE_LABELS: Record<keyof typeof TYPE_COLORS, string> = {
  creatures: 'Creatures',
  instants: 'Instants',
  sorceries: 'Sorceries',
  artifacts: 'Artifacts',
  enchantments: 'Enchantments',
  planeswalkers: 'Planeswalkers',
  lands: 'Lands',
  battles: 'Battles',
  other: 'Other',
};

function TypeBreakdownInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          This chart shows the distribution of <strong>card types</strong> in your deck. Understanding your type breakdown helps you build a
          balanced deck with the right mix of threats, answers, and resources.
        </p>
      </InfoSection>

      <InfoSection title="Card type categories">
        <InfoList
          items={[
            'Creatures: Your threats and blockers',
            'Instants: Reactive spells you can cast anytime',
            'Sorceries: Powerful effects at sorcery speed',
            'Artifacts: Colorless permanents (includes equipment, mana rocks)',
            'Enchantments: Persistent effects and auras',
            'Planeswalkers: Powerful recurring value engines',
            'Lands: Your mana base (shown separately)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Format guidelines">
        <InfoList
          items={[
            'Aggro: 25-30 creatures, minimal non-creature spells',
            'Midrange: 15-20 creatures, balanced removal and value',
            'Control: 5-10 creatures, heavy on instants/sorceries',
            'Commander: More variety; 30+ creatures common in battlecruiser',
          ]}
        />
      </InfoSection>

      <InfoTip>
        A deck that&apos;s all creatures lacks interaction. A deck with no creatures needs alternate win conditions. Balance your types
        based on your strategy — aggro wants more threats, control wants more answers.
      </InfoTip>
    </>
  );
}

export default function TypeBreakdownChart({ data, compact = true }: TypeBreakdownChartProps): ReactElement {
  const chartConfig = useMemo(() => {
    // Build arrays for non-zero types only
    const types = Object.keys(TYPE_COLORS) as (keyof typeof TYPE_COLORS)[];
    const filteredTypes = types.filter((type) => data[type] > 0);

    return {
      labels: filteredTypes.map((type) => TYPE_LABELS[type]),
      datasets: [
        {
          data: filteredTypes.map((type) => data[type]),
          backgroundColor: filteredTypes.map((type) => TYPE_COLORS[type]),
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    };
  }, [data]);

  const options: ChartOptions<'doughnut'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: compact ? '60%' : '50%',
      plugins: {
        legend: {
          display: !compact,
          position: 'right' as const,
          labels: {
            color: '#a8a8b8',
            font: { size: 11 },
            boxWidth: 12,
            padding: 8,
          },
        },
        tooltip: {
          backgroundColor: '#1a1a1f',
          borderColor: '#2a2a32',
          borderWidth: 1,
          titleColor: '#e8e8ef',
          bodyColor: '#a8a8b8',
          padding: 8,
          displayColors: true,
          callbacks: {
            label: (item) => {
              const total = (item.dataset.data as number[]).reduce((a, b) => a + b, 0);
              const pct = total > 0 ? Math.round((Number(item.raw) / total) * 100) : 0;
              return ` ${item.raw} card${Number(item.raw) !== 1 ? 's' : ''} (${pct}%)`;
            },
          },
        },
      },
    }),
    [compact],
  );

  // Don't render if no data
  const hasData = Object.values(data).some((v) => typeof v === 'number' && v > 0);
  if (!hasData) {
    return (
      <div className="text-ash flex h-25 items-center justify-center text-sm">
        <span>No cards</span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-ash text-xs">Card Types</span>
        <InfoButton title="Card Type Breakdown">
          <TypeBreakdownInfo />
        </InfoButton>
      </div>
      <div className={compact ? 'h-25 w-full' : 'h-50 w-full'}>
        <Doughnut data={chartConfig} options={options} />
      </div>
    </div>
  );
}
